<template>
  <div class="supply-inbound">
    <div v-if="pageLoading" class="loading-wrap">
      <van-loading type="spinner" color="#4c9673" />
    </div>
    <template v-else>
      <van-cell-group inset title="订单信息">
        <van-cell title="订单号" :value="orderInfo.orderNo || '-'" />
      </van-cell-group>

      <div class="section-title">
        商品信息
        <span class="section-extra">共 {{ productList.length }} 项</span>
      </div>

      <div
        v-for="(item, index) in productList"
        :key="item.productCode || index"
        class="product-card"
      >
        <div class="product-card__name">
          {{ productDisplayName(item) }}
        </div>
        <div class="product-card__meta">
          <div>编号：{{ item.productCode || '-' }}</div>
          <div>69码：{{ item.barCode || '-' }}</div>
          <div>数量：{{ item.quantity ?? '-' }} {{ item.unit || '' }}</div>
          <div>采购金额：{{ item.purchasePrice ?? '-' }}</div>
        </div>

        <van-field
          v-model="item.retailPrice"
          type="number"
          label="零售价"
          required
          placeholder="请输入零售价"
          input-align="right"
        />
        <van-cell title="毛利率" :value="calcGrossMargin(item)" />
        <van-field
          :value="item.productionDate"
          label="生产日期"
          required
          readonly
          clickable
          placeholder="请选择生产日期"
          input-align="right"
          @click="openDatePicker(index)"
        />
        <van-field
          v-model="item.shelfLifeDays"
          type="digit"
          label="保质期(天)"
          required
          placeholder="请输入保质期"
          input-align="right"
        />
      </div>

      <van-cell-group inset title="入库信息" style="margin-top: 8px">
        <van-cell required is-link @click="stockSelectVisible = true">
          <van-field
            v-model="form.scName"
            label="入库仓库"
            placeholder="请选择入库仓库"
            readonly
            inputmode="none"
            input-align="right"
          />
        </van-cell>
        <van-cell required is-link @click="supplierSelectVisible = true">
          <van-field
            v-model="form.supplierName"
            label="供应商"
            placeholder="请选择供应商"
            readonly
            inputmode="none"
            input-align="right"
          />
        </van-cell>
        <van-field
          v-model="form.description"
          rows="2"
          autosize
          label="备注"
          type="textarea"
          maxlength="500"
          show-word-limit
          placeholder="请输入备注"
        />
      </van-cell-group>

      <div class="footer-placeholder" />
      <div class="footer">
        <van-button
          block
          type="primary"
          color="#4c9673"
          :loading="submitting"
          @click="handleSubmit"
        >
          确认发起入库
        </van-button>
      </div>
    </template>

    <DateTimePicker
      value-format="yyyy-MM-dd"
      :showPicker="showDatePicker"
      @confirm="onDateConfirm"
      @cancel="showDatePicker = false"
    />
    <StockSelect :visible="stockSelectVisible" @confirm="stockSelectConfirm" />
    <SupplierSelect
      :visible="supplierSelectVisible"
      @confirm="supplierSelectConfirm"
    />
  </div>
</template>

<script>
import { Toast } from 'vant'
import {
  getWholesaleOrderProducts,
  pullScmInbound,
} from '../../../service/api/purchase/supply'
import DateTimePicker from '../../../components/dateTimePicker.vue'
import StockSelect from '../../../components/stockSelect.vue'
import SupplierSelect from '../../../components/supplierSelect.vue'

export default {
  name: 'supply-inbound',
  components: {
    DateTimePicker,
    StockSelect,
    SupplierSelect,
  },
  data() {
    return {
      pageLoading: false,
      submitting: false,
      orderInfo: {},
      productList: [],
      form: {
        scId: '',
        scName: '',
        supplierId: '',
        supplierName: '',
        description: '',
      },
      showDatePicker: false,
      dateIndex: -1,
      stockSelectVisible: false,
      supplierSelectVisible: false,
    }
  },
  mounted() {
    this.loadProducts()
  },
  methods: {
    productDisplayName(item) {
      if (!item) return '-'
      const brand = item.brandName ? `【${item.brandName}】` : ''
      return brand + (item.productName || '-')
    },
    /** 毛利率：(零售价 - 采购价 / convertVal) / 零售价，与 PC 一致 */
    calcGrossMargin(row) {
      const purchasePrice = row.purchasePrice
      const retailPrice = row.retailPrice
      if (
        purchasePrice === null ||
        purchasePrice === undefined ||
        purchasePrice === '' ||
        retailPrice === null ||
        retailPrice === undefined ||
        retailPrice === ''
      ) {
        return '-'
      }
      const retail = Number(retailPrice)
      if (!retail || Number.isNaN(retail)) return '-'
      const purchase = Number(purchasePrice)
      if (Number.isNaN(purchase)) return '-'
      const convertVal =
        row.convertVal === null || row.convertVal === undefined || row.convertVal === ''
          ? 1
          : Number(row.convertVal)
      if (!convertVal || Number.isNaN(convertVal)) return '-'
      const margin = this.$utils.div(
        this.$utils.sub(retail, this.$utils.div(purchase, convertVal)),
        retail,
      )
      return `${Number(this.$utils.mul(margin, 100)).toFixed(2)}%`
    },
    loadProducts() {
      const orderNo = this.$route.query.orderNo
      if (!orderNo) {
        Toast.fail('缺少订单号')
        return
      }
      this.pageLoading = true
      getWholesaleOrderProducts({ orderNo })
        .then((res) => {
          const detail = res.data || {}
          this.orderInfo = detail
          this.productList = (detail.items || []).map((item) => ({
            ...item,
            retailPrice: '',
            productionDate: item.productionDate || '',
            shelfLifeDays:
              item.shelfLife === null || item.shelfLife === undefined
                ? ''
                : String(item.shelfLife),
            unit: item.unitName || '',
          }))
        })
        .finally(() => {
          this.pageLoading = false
        })
    },
    openDatePicker(index) {
      this.dateIndex = index
      this.showDatePicker = true
    },
    onDateConfirm(val) {
      if (val && this.dateIndex > -1) {
        this.$set(this.productList[this.dateIndex], 'productionDate', val)
      }
      this.showDatePicker = false
      this.dateIndex = -1
    },
    stockSelectConfirm(row) {
      if (!row || Object.keys(row).length === 0) {
        this.stockSelectVisible = false
        return
      }
      this.stockSelectVisible = false
      this.form.scName = row.name
      this.form.scId = row.id
    },
    supplierSelectConfirm(row) {
      if (!row || Object.keys(row).length === 0) {
        this.supplierSelectVisible = false
        return
      }
      this.supplierSelectVisible = false
      this.form.supplierName = row.name
      this.form.supplierId = row.id
    },
    validate() {
      if (!this.form.scId) {
        Toast.fail('请选择入库仓库')
        return false
      }
      if (!this.form.supplierId) {
        Toast.fail('请选择供应商')
        return false
      }
      for (let i = 0; i < this.productList.length; i++) {
        const item = this.productList[i]
        if (
          item.retailPrice === '' ||
          item.retailPrice === null ||
          item.retailPrice === undefined
        ) {
          Toast.fail(`第${i + 1}行商品零售价不能为空`)
          return false
        }
        if (!item.productionDate) {
          Toast.fail(`第${i + 1}行商品生产日期不能为空`)
          return false
        }
        if (
          item.shelfLifeDays === '' ||
          item.shelfLifeDays === null ||
          item.shelfLifeDays === undefined
        ) {
          Toast.fail(`第${i + 1}行商品保质期不能为空`)
          return false
        }
      }
      return true
    },
    handleSubmit() {
      if (!this.validate()) return
      this.submitting = true
      const params = {
        orderNo: this.orderInfo.orderNo,
        scId: this.form.scId,
        supplierId: this.form.supplierId,
        description: this.form.description,
        products: this.productList.map((item) => ({
          productCode: item.productCode,
          barCode: item.barCode,
          retailPrice: item.retailPrice,
          productionDate: item.productionDate,
          shelfLifeDays: Number(item.shelfLifeDays),
        })),
      }
      pullScmInbound(params)
        .then(() => {
          Toast.success('发起入库成功')
          // 用 back 回到缓存列表，并带上已入库订单号做局部更新
          sessionStorage.setItem('supply-pulled-order-no', this.orderInfo.orderNo || '')
          this.$router.back()
        })
        .finally(() => {
          this.submitting = false
        })
    },
  },
}
</script>

<style lang="less" scoped>
.supply-inbound {
  min-height: 100%;
  background: #f5f5f5;
  padding-bottom: 12px;
}

.loading-wrap {
  padding-top: 80px;
  text-align: center;
}

.section-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 12px 16px 8px;
  font-size: 14px;
  font-weight: 600;
  color: #262626;

  .section-extra {
    font-size: 12px;
    font-weight: 400;
    color: #8c8c8c;
  }
}

.product-card {
  margin: 0 12px 8px;
  padding: 8px 0;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;

  &__name {
    padding: 4px 16px 8px;
    font-size: 14px;
    font-weight: 600;
    color: #262626;
    line-height: 1.4;
  }

  &__meta {
    padding: 0 16px 8px;
    font-size: 12px;
    color: #8c8c8c;
    line-height: 1.7;
  }
}

.footer-placeholder {
  height: 70px;
}

.footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 16px;
  background: #fff;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}

::v-deep .van-cell-group__title {
  padding-top: 12px;
}

::v-deep .van-cell {
  align-items: center;
}
</style>
